import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Database, Play, ChevronDown, ChevronRight, Table, Loader2, AlertCircle, Download, Eye, EyeOff, Trash2, Key, FileJson, RefreshCw, Info } from 'lucide-react';
import toast from 'react-hot-toast';

const API = 'https://api.glowytics.com/api/bigquery';
const STORAGE_KEY = 'bq_explorer_credentials';

function saveCredentials(creds) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(creds)); } catch {}
}
function loadCredentials() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function unwrapValue(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') {
    if (Object.prototype.hasOwnProperty.call(val, 'value')) return String(val.value);
    try { return JSON.stringify(val); } catch { return String(val); }
  }
  return String(val);
}

async function bqFetch(endpoint, body) {
  const res = await fetch(`${API}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Brand-ID': localStorage.getItem('currentBrandId') || '',
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || 'Request failed');
  return data;
}

const AUTH_MODES = [
  { id: 'refresh', label: 'Refresh Token', icon: RefreshCw, desc: 'Kalıcı — süresi dolmaz' },
  { id: 'token', label: 'Access Token', icon: Key, desc: 'Geçici (~1 saat)' },
  { id: 'sa', label: 'Service Account', icon: FileJson, desc: 'JSON key dosyası' },
];

export default function BigQueryExplorer() {
  const saved = loadCredentials();
  const [projectId, setProjectId] = useState(saved?.projectId || '');
  const [location, setLocation] = useState(saved?.location || 'US');
  const [authMode, setAuthMode] = useState(saved?.authMode || 'refresh');
  const [accessToken, setAccessToken] = useState(saved?.accessToken || '');
  const [refreshToken, setRefreshToken] = useState(saved?.refreshToken || '');
  const [saJson, setSaJson] = useState(saved?.saJson || '');
  const [showSecret, setShowSecret] = useState(false);
  const [connected, setConnected] = useState(false);
  const [dbConnected, setDbConnected] = useState(false);

  const [datasets, setDatasets] = useState([]);
  const [expandedDs, setExpandedDs] = useState({});
  const [dsTables, setDsTables] = useState({});
  const [sidebarLoading, setSidebarLoading] = useState(false);

  const [sql, setSql] = useState(saved?.lastSql || 'SELECT 1 AS test');
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState('');
  const [queryTime, setQueryTime] = useState(null);
  const [rowCount, setRowCount] = useState(0);

  const textareaRef = useRef(null);

  // Load stored connection from DB on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('https://api.glowytics.com/api/auth/bigquery/connection', {
          credentials: 'include',
          headers: { 'X-Brand-ID': localStorage.getItem('currentBrandId') || '' },
        });
        const data = await res.json();
        if (data.connected) {
          setDbConnected(true);
          if (data.refreshToken && !refreshToken) { setRefreshToken(data.refreshToken); setAuthMode('refresh'); }
          if (data.accessToken && !accessToken && !data.refreshToken) { setAccessToken(data.accessToken); setAuthMode('token'); }
          if (data.projectId && !projectId) setProjectId(data.projectId);
        }
      } catch {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const creds = useCallback(() => {
    const base = { projectId, location };
    if (authMode === 'refresh' && refreshToken) base.refreshToken = refreshToken;
    if (authMode === 'token' && accessToken) base.accessToken = accessToken;
    if (authMode === 'sa' && saJson) base.serviceAccountJson = saJson;
    return base;
  }, [projectId, location, authMode, accessToken, refreshToken, saJson]);

  useEffect(() => {
    saveCredentials({ projectId, location, authMode, accessToken, refreshToken, saJson, lastSql: sql });
  }, [projectId, location, authMode, accessToken, refreshToken, saJson, sql]);

  const hasAuth = authMode === 'refresh' ? !!refreshToken.trim()
    : authMode === 'token' ? !!accessToken.trim()
    : !!saJson.trim();

  const handleConnect = async () => {
    if (!projectId.trim()) { toast.error('Project ID gerekli'); return; }
    if (!hasAuth) { toast.error('Kimlik bilgilerini doldurun'); return; }
    setSidebarLoading(true);
    try {
      const data = await bqFetch('/datasets', creds());
      setDatasets(data.datasets || []);
      setConnected(true);
      toast.success(`${data.datasets?.length || 0} dataset bulundu`);
    } catch (e) {
      toast.error(e.message);
      setConnected(false);
    } finally { setSidebarLoading(false); }
  };

  const handleToggleDs = async (dsId) => {
    if (expandedDs[dsId]) { setExpandedDs(p => ({ ...p, [dsId]: false })); return; }
    setExpandedDs(p => ({ ...p, [dsId]: true }));
    if (dsTables[dsId]) return;
    try {
      const data = await bqFetch('/tables', { ...creds(), datasetId: dsId });
      setDsTables(p => ({ ...p, [dsId]: data.tables || [] }));
    } catch (e) { toast.error(`Tablolar yüklenemedi: ${e.message}`); }
  };

  const insertTableRef = (dsId, tableId) => {
    const ref = `\`${projectId}.${dsId}.${tableId}\``;
    if (textareaRef.current) {
      const ta = textareaRef.current;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newSql = sql.substring(0, start) + ref + sql.substring(end);
      setSql(newSql);
      setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + ref.length; }, 0);
    } else {
      setSql(p => p + ' ' + ref);
    }
  };

  const handleRunQuery = async () => {
    if (!sql.trim()) { toast.error('SQL sorgusu boş olamaz'); return; }
    setQueryLoading(true); setQueryError(''); setRows([]); setColumns([]); setQueryTime(null); setRowCount(0);
    const t0 = performance.now();
    try {
      const data = await bqFetch('/query', { ...creds(), sql: sql.trim() });
      const elapsed = ((performance.now() - t0) / 1000).toFixed(2);
      setQueryTime(elapsed);
      const resultRows = data.rows || [];
      setRowCount(data.rowCount || resultRows.length);
      if (resultRows.length > 0) { setColumns(Object.keys(resultRows[0])); setRows(resultRows); }
      toast.success(`${resultRows.length} satır — ${elapsed}s`);
    } catch (e) { setQueryError(e.message); toast.error(e.message); }
    finally { setQueryLoading(false); }
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); handleRunQuery(); }
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    const header = columns.join(',');
    const body = rows.map(r =>
      columns.map(c => {
        const v = unwrapValue(r[c]);
        return v.includes(',') || v.includes('"') || v.includes('\n') ? `"${v.replace(/"/g, '""')}"` : v;
      }).join(',')
    ).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `bigquery_export_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    setProjectId(''); setLocation('US'); setAccessToken(''); setRefreshToken(''); setSaJson('');
    setConnected(false); setDatasets([]); setExpandedDs({}); setDsTables({});
    localStorage.removeItem(STORAGE_KEY);
    toast.success('Temizlendi');
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f5f5f7]">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white border border-gray-200/80 rounded-2xl flex items-center justify-center shadow-sm">
            <Database className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">BigQuery Explorer</h1>
            <p className="text-xs text-gray-500">
              BigQuery'den veri çekin ve analiz edin
              {dbConnected && <span className="ml-2 text-emerald-600 font-medium">— Kayıtlı bağlantı yüklendi</span>}
            </p>
          </div>
        </div>
        {connected && (
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-xs text-gray-500">Bağlı</span>
          </div>
        )}
      </div>

      {/* Credentials Panel */}
      <div className="rounded-3xl border border-gray-200/80 bg-white p-5 shadow-sm mb-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-gray-700">Bağlantı Bilgileri</h2>
          {(projectId || accessToken || refreshToken || saJson) && (
            <button onClick={handleClear} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-red-600 bg-red-50 border border-red-100 hover:bg-red-100 transition-colors">
              <Trash2 className="w-3 h-3" /> Temizle
            </button>
          )}
        </div>

        {/* Project ID + Location + Connect */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Project ID *</label>
            <input type="text" value={projectId} onChange={e => setProjectId(e.target.value)}
              placeholder="my-gcp-project"
              className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Location</label>
            <select value={location} onChange={e => setLocation(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow appearance-none">
              <option value="US">US</option>
              <option value="EU">EU</option>
              <option value="us-central1">us-central1</option>
              <option value="us-east1">us-east1</option>
              <option value="europe-west1">europe-west1</option>
              <option value="europe-west2">europe-west2</option>
              <option value="asia-east1">asia-east1</option>
              <option value="asia-northeast1">asia-northeast1</option>
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={handleConnect} disabled={sidebarLoading || !projectId.trim() || !hasAuth}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 text-white font-medium rounded-xl px-4 py-2.5 text-sm transition-colors flex items-center justify-center gap-2 shadow-sm">
              {sidebarLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              {connected ? 'Yenile' : 'Bağlan'}
            </button>
          </div>
        </div>

        {/* Auth Mode Tabs */}
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-2">Kimlik Doğrulama Yöntemi</label>
          <div className="flex flex-wrap gap-2">
            {AUTH_MODES.map(m => {
              const Icon = m.icon;
              const active = authMode === m.id;
              return (
                <button key={m.id} onClick={() => { setAuthMode(m.id); setShowSecret(false); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                    active
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
                  }`}>
                  <Icon className="w-4 h-4" />
                  <span>{m.label}</span>
                  <span className={`text-[10px] ${active ? 'text-blue-200' : 'text-gray-400'}`}>({m.desc})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Auth: Refresh Token */}
        {authMode === 'refresh' && (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs text-gray-500">Refresh Token *</label>
                <button onClick={() => setShowSecret(!showSecret)} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors">
                  {showSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showSecret ? 'Gizle' : 'Göster'}
                </button>
              </div>
              <input type={showSecret ? 'text' : 'password'} value={refreshToken} onChange={e => setRefreshToken(e.target.value)}
                placeholder="1//0abc..."
                className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
            </div>

            <div className="flex items-start gap-2.5 bg-blue-50/60 border border-blue-100 rounded-xl px-4 py-3">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-700 leading-relaxed">
                <p className="font-medium mb-1">Refresh Token nasıl alınır? (Client ID / Secret gerekmez!)</p>
                <ol className="list-decimal ml-4 space-y-1 text-blue-600">
                  <li><a href="https://developers.google.com/oauthplayground/" target="_blank" rel="noopener noreferrer" className="underline font-medium">developers.google.com/oauthplayground</a> adresine gidin</li>
                  <li>Sol panelde <strong>"BigQuery API v2"</strong> bulun ve <code className="bg-blue-100 px-1 rounded">https://www.googleapis.com/auth/bigquery</code> scope'unu seçin</li>
                  <li><strong>"Authorize APIs"</strong> butonuna tıklayın</li>
                  <li>BigQuery erişimi olan Google hesabınızla giriş yapıp izin verin</li>
                  <li><strong>"Exchange authorization code for tokens"</strong> butonuna tıklayın</li>
                  <li>Sağ panelde görünen <strong>Refresh token</strong> değerini kopyalayıp yukarıya yapıştırın</li>
                </ol>
                <p className="mt-2 text-blue-500">Refresh token kalıcıdır, süresi dolmaz. Bir kere alın, sürekli kullanın.</p>
              </div>
            </div>
          </div>
        )}

        {/* Auth: Access Token */}
        {authMode === 'token' && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs text-gray-500">Access Token *</label>
              <button onClick={() => setShowSecret(!showSecret)} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors">
                {showSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showSecret ? 'Gizle' : 'Göster'}
              </button>
            </div>
            <input type={showSecret ? 'text' : 'password'} value={accessToken} onChange={e => setAccessToken(e.target.value)}
              placeholder="ya29.a0AfH6SMB..."
              className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
            <div className="mt-3 flex items-start gap-2.5 bg-amber-50/60 border border-amber-100 rounded-xl px-4 py-3">
              <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                Token ~1 saat geçerlidir. <code className="bg-amber-100 px-1 rounded">gcloud auth print-access-token</code> veya BigQuery Console &rarr; F12 &rarr; Network &rarr; Authorization header'dan alabilirsiniz.
              </p>
            </div>
          </div>
        )}

        {/* Auth: Service Account */}
        {authMode === 'sa' && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs text-gray-500">Service Account JSON</label>
              <button onClick={() => setShowSecret(!showSecret)} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors">
                {showSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showSecret ? 'Gizle' : 'Göster'}
              </button>
            </div>
            {showSecret ? (
              <textarea value={saJson} onChange={e => setSaJson(e.target.value)}
                placeholder='{"type":"service_account","project_id":"...","private_key":"...","client_email":"..."}'
                rows={4}
                className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow resize-y" />
            ) : (
              <div onClick={() => setShowSecret(true)}
                className="w-full bg-white border border-gray-200 rounded-xl px-3.5 py-2.5 text-xs text-gray-400 cursor-pointer hover:border-blue-300 transition-colors">
                {saJson ? '••••••••  (tıklayarak düzenleyin)' : 'JSON yapıştırmak için tıklayın...'}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Left — Dataset/Table browser */}
        <div className="lg:w-72 flex-shrink-0">
          <div className="rounded-3xl border border-gray-200/80 bg-white overflow-hidden shadow-sm">
            <div className="px-4 py-3.5 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-700">Datasets & Tables</h3>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {!connected && (
                <div className="px-4 py-10 text-center text-xs text-gray-400">Bağlanarak dataset ve tabloları görüntüleyin</div>
              )}
              {connected && datasets.length === 0 && (
                <div className="px-4 py-10 text-center text-xs text-gray-400">Dataset bulunamadı</div>
              )}
              {datasets.map(ds => (
                <div key={ds.id}>
                  <button onClick={() => handleToggleDs(ds.id)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-blue-50/50 text-left text-sm transition-colors">
                    {expandedDs[ds.id]
                      ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                    <Database className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                    <span className="truncate text-gray-700 font-medium">{ds.id}</span>
                  </button>
                  {expandedDs[ds.id] && (
                    <div className="ml-5 border-l border-gray-100">
                      {!dsTables[ds.id] ? (
                        <div className="px-4 py-2 text-xs text-gray-400 flex items-center gap-1.5">
                          <Loader2 className="w-3 h-3 animate-spin" /> Yükleniyor...
                        </div>
                      ) : dsTables[ds.id].length === 0 ? (
                        <div className="px-4 py-2 text-xs text-gray-400">Tablo yok</div>
                      ) : (
                        dsTables[ds.id].map(t => (
                          <button key={t.id} onClick={() => insertTableRef(ds.id, t.id)}
                            title={`SQL'e ekle: ${projectId}.${ds.id}.${t.id}`}
                            className="w-full flex items-center gap-2 px-4 py-1.5 hover:bg-blue-50/50 text-left text-xs transition-colors group">
                            <Table className="w-3 h-3 text-blue-500 flex-shrink-0" />
                            <span className="truncate text-gray-500 group-hover:text-gray-800">{t.id}</span>
                            {t.rowCount && <span className="ml-auto text-[10px] text-gray-400">{Number(t.rowCount).toLocaleString()}</span>}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main — SQL Editor + Results */}
        <div className="flex-1 min-w-0 flex flex-col gap-5">
          {/* SQL Editor */}
          <div className="rounded-3xl border border-gray-200/80 bg-white overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-700">SQL Query</span>
              <div className="flex items-center gap-3">
                <span className="px-2 py-0.5 text-[10px] rounded-lg bg-gray-100 text-gray-500 border border-gray-200">Ctrl+Enter</span>
                <button onClick={handleRunQuery} disabled={queryLoading || !sql.trim()}
                  className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-100 disabled:text-gray-400 text-white text-xs font-medium rounded-xl px-4 py-2 transition-colors shadow-sm">
                  {queryLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                  Çalıştır
                </button>
              </div>
            </div>
            <textarea ref={textareaRef} value={sql} onChange={e => setSql(e.target.value)} onKeyDown={handleKeyDown}
              rows={8} spellCheck={false}
              className="w-full bg-[#f8f9fa] px-5 py-4 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none resize-y border-none"
              placeholder="SELECT * FROM `project.dataset.table` LIMIT 100" />
          </div>

          {/* Results */}
          <div className="rounded-3xl border border-gray-200/80 bg-white overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Sonuçlar</span>
                {rowCount > 0 && (
                  <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 rounded-lg px-2 py-0.5 font-medium">
                    {rowCount.toLocaleString()} satır
                  </span>
                )}
                {queryTime && <span className="text-[10px] text-gray-400">{queryTime}s</span>}
              </div>
              {rows.length > 0 && (
                <button onClick={exportCsv} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm">
                  <Download className="w-3 h-3" /> CSV İndir
                </button>
              )}
            </div>

            {queryError && (
              <div className="px-5 py-3 bg-red-50 border-b border-red-100 flex items-start gap-2.5">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <pre className="text-xs text-red-600 whitespace-pre-wrap break-all">{queryError}</pre>
              </div>
            )}

            {queryLoading && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                <span className="ml-2 text-sm text-gray-500">Sorgu çalışıyor...</span>
              </div>
            )}

            {!queryLoading && rows.length === 0 && !queryError && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Database className="w-8 h-8 mb-2 opacity-30" />
                <span className="text-xs">Sorgu çalıştırarak sonuçları burada görün</span>
              </div>
            )}

            {!queryLoading && rows.length > 0 && (
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50/90 backdrop-blur-sm z-10">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-[10px] text-gray-500 font-semibold border-b border-gray-200 w-10">#</th>
                      {columns.map(col => (
                        <th key={col} className="px-3 py-2.5 text-left text-[10px] text-gray-500 font-semibold border-b border-gray-200 whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i} className="hover:bg-blue-50/30 transition-colors">
                        <td className="px-3 py-2 text-gray-400 border-b border-gray-100">{i + 1}</td>
                        {columns.map(col => (
                          <td key={col} className="px-3 py-2 text-gray-700 border-b border-gray-100 whitespace-nowrap max-w-xs truncate" title={unwrapValue(row[col])}>
                            {unwrapValue(row[col])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
